import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import client from '../lib/api';
import { applySiteTheme, cacheSiteTheme } from '../lib/theme';
import { syncSkinFlag } from '../lib/betrunSkin';
import { applyFavicon } from '../lib/favicon';
import { cacheBrand } from '../lib/brand';
import { fillSplash, hideSplash } from '../lib/splash';
import { readSettingsPrefetch } from '../lib/settingsPrefetch';
import { applyEntryRedirect } from '../lib/entryDomain';
import { adminRoutesVisible } from '../lib/adminDomain';

const SettingsContext = createContext(null);
export const SETTINGS_UPDATED_EVENT = 'app:settings-updated';
const SETTINGS_UPDATED_STORAGE_KEY = 'app:settings-updated-at';

// Sunucu yeniden başlarken (kod güncellemesi, servis yenileme) ters vekil
// birkaç saniye 502/503/504 döner; ağ kopukluğunda ise yanıt hiç gelmez.
// Bu, uygulamanın döndürdüğü sıradan bir hatadan (401, 404, 422) ayrılmalıdır:
// orada ayarlar gelmiştir ve site çalışır, burada elde hiçbir şey yoktur ve
// site yarım açılırsa oyuncu "site bozuldu" der.
const UNREACHABLE_STATUSES = new Set([502, 503, 504, 521, 522, 523, 524]);

export const isServerUnreachable = (error) => {
  // İptal edilen istek (sayfa değişimi, StrictMode) kesinti değildir.
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return false;
  const status = error?.response?.status;
  // Yanıt hiç gelmediyse (ağ hatası, zaman aşımı) sunucuya ulaşılamıyordur.
  if (status === undefined) return true;
  return UNREACHABLE_STATUSES.has(status);
};

// Kesinti sürerken ayarlar 30 saniyelik olağan turdan daha sık yoklanır:
// güncelleme genelde yarım dakikada biter ve oyuncu sayfayı elle yenilemek
// zorunda kalmamalıdır.
export const UNREACHABLE_RETRY_MS = 3000;

export const notifySettingsUpdated = () => {
  window.dispatchEvent(new Event(SETTINGS_UPDATED_EVENT));
  try {
    localStorage.setItem(SETTINGS_UPDATED_STORAGE_KEY, String(Date.now()));
  } catch {
    // Bazı gizli/katı tarayıcı oturumlarında localStorage kullanılamayabilir.
  }
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [unreachable, setUnreachable] = useState(false);

  const refreshSettings = useCallback(async () => {
    // İlk tur, index.html'in paketten önce başlattığı isteği kullanır; aynı
    // cevabı ikinci kez istemek telefonda açılışı uzatırdı. Başarısızsa
    // olağan isteğe düşülür (bkz. lib/settingsPrefetch.js).
    const prefetched = await readSettingsPrefetch();
    if (prefetched) {
      setSettings(prefetched);
      setUnreachable(false);
      return prefetched;
    }
    try {
      const { data } = await client.get('/settings', {
        params: { _: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setSettings(data);
      setUnreachable(false);
      return data;
    } catch (error) {
      setUnreachable(isServerUnreachable(error));
      setSettings((current) => current || {});
      return null;
    }
  }, []);

  useEffect(() => {
    refreshSettings();
    const handleSettingsUpdated = () => refreshSettings();
    const handleStorage = (event) => {
      if (event.key === SETTINGS_UPDATED_STORAGE_KEY) refreshSettings();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshSettings();
    };
    window.addEventListener(SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleSettingsUpdated);
    document.addEventListener('visibilitychange', handleVisibility);
    const interval = window.setInterval(refreshSettings, 30000);
    return () => {
      window.removeEventListener(SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleSettingsUpdated);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.clearInterval(interval);
    };
  }, [refreshSettings]);

  // Kesintide daha sık yoklanır ve sunucu döndüğü anda ekran kendiliğinden
  // devam eder; oyuncudan sayfayı yenilemesi istenmez.
  useEffect(() => {
    if (!unreachable) return undefined;
    const timer = window.setInterval(refreshSettings, UNREACHABLE_RETRY_MS);
    return () => window.clearInterval(timer);
  }, [unreachable, refreshSettings]);

  useEffect(() => {
    if (!settings) return;
    // Giriş adresi değiştiyse ziyaretçi yayındaki adrese taşınır (bkz.
    // lib/entryDomain.js). Karar sunucudan gelir ve taşınma sayfayı
    // terk ettiği için tema/marka işlerinden **önce** yapılır: engellenen
    // adreste boş yere bir kare çizmenin anlamı yok.
    if (applyEntryRedirect(settings.entry_domain, adminRoutesVisible(settings))) return;
    applySiteTheme(settings.primary_color, settings.secondary_color, settings.site_theme);
    cacheSiteTheme(settings.primary_color, settings.secondary_color, settings.site_theme);
    // Betrun'un paketlenmiş görünümü yalnızca özel CSS/JS'i olmayan kurulumda
    // açılır (bkz. lib/betrunSkin.js).
    syncSkinFlag(settings);
    // Marka, ilk boyamada kullanılabilsin diye saklanır (bkz. lib/brand.js);
    // panelden boşaltılırsa önbellek de temizlenir.
    // Önbellek boşken (ilk ziyaret, yeni alan adı) açılış ekranı markasız
    // kalmış olabilir; ekran söndürülmeden önce doldurulur (bkz. lib/splash.js).
    fillSplash(cacheBrand(settings));
    if (!window.location.pathname.startsWith('/sayfa/')) {
      document.title = settings.site_name || '';
    }
    applyFavicon(settings.favicon_url);
    // Tema, marka ve simge yerine oturdu: açılış ekranı artık kalkabilir.
    // Ayarlar hiç gelmese de (`refreshSettings` hata yolunda `{}` yazar) ekran
    // açılır; oyuncuyu sonsuza dek logoya bakarken bırakmak en kötüsüdür.
    // Hiçbir şemada ek bekleme yoktur (bkz. lib/splash.js).
    hideSplash();
  }, [settings]);

  // Elde hiç ayar yokken yaşanan kesinti siteyi kullanılamaz kılar; ayarlar
  // bir kez geldikten sonraki kısa kopukluk ise oyuncunun okuduğu sayfayı
  // kaplamamalıdır (o sırada yapılan istek zaten kendi hatasını gösterir).
  const hasSettings = settings !== null && Object.keys(settings).length > 0;

  return (
    <SettingsContext.Provider value={{
      ...(settings || {}),
      settingsLoaded: settings !== null,
      serverUnreachable: unreachable && !hasSettings,
      refreshSettings,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext) || {};
